import { z } from 'zod';
import { fragranceDataset } from '../domain/fragrance/catalogue';
import type { Preferences } from '../domain/preferences';
import type { Recommendation } from '../domain/recommendation/engine';
import type { StyleProfile } from '../domain/style/schema';

const PROMPT_VERSION = 'recommendation-copy-v2';
const CACHE_PREFIX = 'style.recommendation-copy.v2.';

const responseSchema = z.object({
  descriptions: z.record(z.string(), z.string().min(80).max(700)),
  provider: z.string(),
  model: z.string(),
  promptVersion: z.literal(PROMPT_VERSION),
  generatedAt: z.string(),
});

const stableHash = (value: string) => {
  let result = 2166136261;
  for (const character of value) result = Math.imul(result ^ character.charCodeAt(0), 16777619);
  return (result >>> 0).toString(36);
};

const requestFor = (
  recommendations: Recommendation[],
  style: StyleProfile,
  preferences: Preferences,
) => ({
  promptVersion: PROMPT_VERSION,
  catalogueVersion: fragranceDataset.dataset_metadata.schema_version,
  styleLabelIds: style.calibratedCoreLabelIds,
  supportingLabelIds: style.calibratedSupportingLabelIds,
  preferenceCues: {
    likedAccords: preferences.likedAccords.slice(0, 12),
    season: preferences.season,
    occasion: preferences.occasion,
    intensity: preferences.intensity,
  },
  items: recommendations.map((item) => ({
    fragranceId: item.fragrance.fragrance_id,
    role: item.role,
    matchedSignalIds: item.score.matchedSignalIds.slice(0, 8),
  })),
});

const applyCopy = (
  recommendations: Recommendation[],
  descriptions: Record<string, string>,
): Recommendation[] =>
  recommendations.map((item) => {
    const description = descriptions[item.fragrance.fragrance_id];
    return description
      ? { ...item, why: description, copySource: 'ai', copyVersion: PROMPT_VERSION }
      : item;
  });

export const enrichRecommendationCopy = async (
  recommendations: Recommendation[],
  style: StyleProfile,
  preferences: Preferences,
): Promise<Recommendation[]> => {
  if (recommendations.length !== 5 || typeof window === 'undefined') return recommendations;
  const request = requestFor(recommendations, style, preferences);
  const key = `${CACHE_PREFIX}${stableHash(JSON.stringify(request))}`;
  try {
    const cached = window.localStorage.getItem(key);
    if (cached) {
      const parsed = responseSchema.safeParse(JSON.parse(cached));
      if (parsed.success) return applyCopy(recommendations, parsed.data.descriptions);
    }
    const response = await fetch('/api/recommendations/copy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) return recommendations;
    const parsed = responseSchema.safeParse(await response.json());
    if (!parsed.success) return recommendations;
    const expectedIds = recommendations.map((item) => item.fragrance.fragrance_id);
    if (expectedIds.some((id) => !parsed.data.descriptions[id])) return recommendations;
    // Cache only generated text and provider metadata. No images, catalogue rows or history.
    window.localStorage.setItem(key, JSON.stringify(parsed.data));
    return applyCopy(recommendations, parsed.data.descriptions);
  } catch {
    return recommendations;
  }
};
